import { MenuItem } from 'primeng/api';
import { utils } from '@app/shared';
import { Ticket } from 'src/app/models';
import { TcTicketService } from 'src/app/shared/services';

/**
 * Menu de contexto (botão direito) do card, com rótulos e ícones copiados do menu da sprint.
 * Existe porque o clique esquerdo abre o modal de histórico — sem o botão direito, esta tela não
 * teria rota nenhuma para o chamado.
 */

export interface TicketLogContextMenuDeps {
  ticketService: TcTicketService;
  /** URL absoluta do chamado, já serializada pelo Router de quem chama. */
  buildTicketUrl: (ticket: Ticket) => string;
  notify: (mensagem: string) => void;
}

/**
 * Usa `makeNote` com os campos avulsos em vez de `copyNote(pub, ticket)`.
 *
 * ⚠️ `copyNote` lê `ticket.customer?.ref`, mas o `TicketResource` desta tela devolve
 * `"customer" => customer->person` — a PESSOA, não o cliente. O código certo é o campo achatado
 * `customer_code`, o mesmo que a sprint usa; sem isso a linha "Cliente:" sai vazia, sem erro.
 *
 * `executor` fica de fora para a saída bater com a da sprint, que não tem essa linha.
 */
function montarRecado(ticket: Ticket, ticketService: TcTicketService, pub: boolean): string {
  return ticketService.makeNote({
    ticketNumber: ticket.ticket_number_teorema ?? '',
    ticketUUID: ticket.id,
    summary: ticket.summary,
    problem: ticket.problem,
    customerRef: ticket.customer_code,
    customerName: ticket.customer_name,
    createdAt: ticket.created_at,
    expectedDate: ticket.expected_date,
    system: ticket.system?.description,
    requester: ticket.requester,
    status: ticket.status?.description,
    responsible: ticket.responsible?.name,
    pub,
    privateUrl: `${window.location.origin}/v3ndor/chamados/${ticket.ticket_number_teorema}`,
  });
}

export function buildTicketLogContextMenu(
  ticket: Ticket,
  deps: TicketLogContextMenuDeps
): MenuItem[] {
  const { ticketService, buildTicketUrl, notify } = deps;

  return [
    {
      label: 'Copiar número',
      icon: 'pi pi-hashtag',
      command: () => {
        ticketService.copyTicketNumber(ticket);
        notify('Número do chamado copiado!');
      },
    },
    {
      label: 'Copiar recado (Privado)',
      icon: 'pi pi-lock',
      command: () => {
        utils.copyText(montarRecado(ticket, ticketService, false));
        notify('Recado privado copiado!');
      },
    },
    {
      label: 'Copiar recado (Público)',
      icon: 'pi pi-globe',
      command: () => {
        utils.copyText(montarRecado(ticket, ticketService, true));
        notify('Recado público copiado!');
      },
    },
    {
      label: 'Abrir em nova aba',
      icon: 'pi pi-external-link',
      command: () => {
        window.open(buildTicketUrl(ticket), '_blank');
      },
    },
  ];
}
